/* SEO actions view: the impact-versus-effort scatter and the action cards.
   Ported verbatim from artifact.html; grouping, packets, priority and impact are computed
   server-side now.

   The impact tile keeps the recover/capture split exactly: actions whose pages already
   rank show recoverable clicks per month, everything else shows impressions of demand
   addressed and never projects clicks. */

import { ACTIONS, EFFORT, RECOVER } from "@oqc/classifier";
import { $, esc, fmt, scase } from "../util.js";
import { paintIcons } from "../icons.js";
import { PRIORITY_COLORS } from "../colors.js";

export function renderActions(actions, ctx) {
  renderActionMatrix(actions);

  $("action-cards").innerHTML = actions.map(a => {
    const [pc, pb] = PRIORITY_COLORS[a.priority] || PRIORITY_COLORS.Low;
    const st = a.stats;
    const issue = a.key === "clean"
      ? fmt(st.n) + " machine / junk queries are mixed into the query set."
      : fmt(st.n) + " queries (" + fmt(st.imp) + " impressions) at avg position " + st.pos.toFixed(1) +
        (st.clicks === 0 ? " with zero clicks captured." : " capturing only " + fmt(st.clicks) + " clicks.");

    const heroMain = a.impact.type === "hygiene"
      ? '<div class="ahv main neutral"><b>—</b><span>data hygiene</span></div>'
      : a.impact.type === "recover"
        ? (a.impact.value > 0
          ? '<div class="ahv main"><b>+' + fmt(a.impact.value) + '</b><span>clicks/mo recoverable</span></div>'
          : '<div class="ahv main neutral"><b>hold</b><span>defends capture</span></div>')
        : '<div class="ahv main" style="background:var(--violet-soft);color:var(--violet)"><b>' + fmt(a.impact.value) + '</b><span>im/mo demand addressed</span></div>';

    const pkHtml = a.packets.length
      ? '<div class="pklabel">Work packets</div>' + a.packets.map(p =>
          '<div class="pk"><div class="pkh">&#8220;' + esc(scase(p.h1)) + '&#8221;</div>' +
          '<div class="pkm"><b>' + fmt(p.n) + "</b> queries · <b>" + fmt(p.imp) + "</b> im · " +
          (p.target ? "optimise " + esc(p.target.replace(/^https?:\/\/[^/]+/, "")) : "new " + (p.formatHint || "content")) +
          "</div></div>").join("") +
        (a.packetCount > a.packets.length ? '<div class="pkm" style="margin-top:2px">+ ' + (a.packetCount - a.packets.length) + " more in the work order CSV</div>" : "")
      : "";

    return '<div class="acard" data-ac="' + a.key + '">' +
      '<div style="display:flex;justify-content:space-between;align-items:center"><div class="ah"><i data-ic="' + a.icon + '"></i>' + a.title +
      '</div><span class="pill" style="color:' + pc + ";background:" + pb + ';font-weight:700">' + a.priority + "</span></div>" +
      '<div class="ahero">' + heroMain +
      '<div class="ahv q"><b>' + fmt(st.n) + '</b><span>queries</span></div>' +
      '<div class="ahv i"><b>' + fmt(st.imp) + '</b><span>impr · pos ' + st.pos.toFixed(1) + "</span></div></div>" +
      '<div class="af">' +
      '<span class="fl">Issue</span><span class="fv">' + issue + "</span>" +
      '<span class="fl">Recommend</span><span class="fv"><b>' + a.description + "</b></span>" +
      '<span class="fl">Why it matters</span><span class="fv">' + a.why + "</span>" +
      '<span class="fl">Effort</span><span class="fv">' + a.effort.label +
        (a.key !== "clean" ? " · " + (a.impact.type === "recover" ? "existing rankings" : "net-new content") : "") + "</span>" +
      '<span class="fl">Target</span><span class="fv">' + a.target + "</span>" +
      "</div>" + pkHtml +
      '<div class="aq">' + a.topQueries.map(d => "<span>" + esc(d.q.length > 44 ? d.q.slice(0, 44) + "…" : d.q) + "</span>").join("") + "</div>" +
      '<div style="display:flex;gap:8px"><span class="abtn" data-da="' + a.key + '"><i data-ic="table"></i>view queries</span>' +
      (a.key !== "clean" ? '<span class="abtn" data-wo="' + a.key + '"><i data-ic="dl"></i>work order CSV</span>' : "") +
      "</div></div>";
  }).join("") || '<div class="note">No actionable queries in this dataset.</div>';

  paintIcons($("action-cards"));
  document.querySelectorAll("[data-da]").forEach(el =>
    el.addEventListener("click", () => ctx.onFilter({ action: el.dataset.da })));
  document.querySelectorAll("[data-wo]").forEach(el =>
    el.addEventListener("click", () => ctx.onExportAction(el.dataset.wo)));
}

/* Impact against effort, with the quick-wins corner marked. Dots are green for actions
   that recover clicks and violet for those that capture new demand. */
function renderActionMatrix(actions) {
  const W = 680, H = 250, L = 46, B = 34, T = 14, R = 14;
  const pw = W - L - R, ph = H - T - B;
  const dots = actions.filter(a => a.key !== "clean");
  const maxImp = Math.max(...dots.map(a => a.stats.imp), 1);
  const maxN = Math.max(...dots.map(a => a.stats.n), 1);
  const x = tier => L + (tier - 0.5) / 4 * pw;
  const y = imp => T + ph - Math.sqrt(imp / maxImp) * ph;
  let svg = "";

  const tiers = ["On-page", "Content edit", "New content", "Process"];
  for (let t = 1; t <= 4; t++) {
    svg += '<text x="' + x(t) + '" y="' + (H - 14) + '" text-anchor="middle" font-family="Figtree" font-weight="700" font-size="9.5" fill="#57575F">' + tiers[t - 1] + "</text>";
    if (t < 4) svg += '<line x1="' + (L + t / 4 * pw) + '" y1="' + T + '" x2="' + (L + t / 4 * pw) + '" y2="' + (T + ph) +
      '" stroke="#EAEAEF" stroke-width="1"' + (t === 2 ? ' stroke-dasharray="4 4" stroke="#DDDDE3"' : "") + "/>";
  }
  svg += '<text x="' + (L - 34) + '" y="' + (T + ph / 2) + '" transform="rotate(-90 ' + (L - 34) + " " + (T + ph / 2) +
    ')" text-anchor="middle" font-family="Figtree" font-size="8.5" fill="#8A8A93">DEMAND (IMPRESSIONS)</text>';
  svg += '<rect x="' + L + '" y="' + T + '" width="' + (pw / 2) + '" height="' + (ph / 2) + '" fill="var(--green)" opacity=".045"/>' +
    '<text x="' + (L + 8) + '" y="' + (T + 14) + '" font-family="Figtree" font-weight="800" font-size="9.5" fill="#16A34A" opacity=".8">QUICK WINS</text>';

  const byTier = {};
  for (const a of dots) (byTier[a.effort.tier] = byTier[a.effort.tier] || []).push(a);
  for (const t of Object.keys(byTier)) {
    byTier[t].forEach((a, i) => {
      const jx = x(+t) + (i - (byTier[t].length - 1) / 2) * 26;
      const jy = y(a.stats.imp);
      const r = 6 + Math.sqrt(a.stats.n / maxN) * 9;
      const col = RECOVER[a.key] ? "#16A34A" : "#5509F8";
      svg += '<g class="jnode" data-mx="' + a.key + '" style="cursor:pointer">' +
        '<circle cx="' + jx.toFixed(1) + '" cy="' + jy.toFixed(1) + '" r="' + r.toFixed(1) + '" fill="' + col + '" opacity=".7"/>' +
        '<text x="' + jx.toFixed(1) + '" y="' + (jy - r - 5).toFixed(1) + '" text-anchor="middle" font-family="Figtree" font-weight="700" font-size="9.5" fill="#0A0A0B">' + a.title + "</text>" +
        "<title>" + a.title + ": " + fmt(a.stats.n) + " queries, " + fmt(a.stats.imp) + " impressions, " + a.effort.label + "</title></g>";
    });
  }
  $("action-matrix").innerHTML = svg;
  document.querySelectorAll("[data-mx]").forEach(el => el.addEventListener("click", () => {
    const card = document.querySelector('[data-ac="' + el.dataset.mx + '"]');
    if (card) card.scrollIntoView({ behavior: "smooth", block: "center" });
  }));
}

export { ACTIONS, EFFORT };
